import React, { FC, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../hooks';
import { setAvatar, deleteAvatar } from '../reducers/userReducer';
import { notify } from '../reducers/notificationReducer';
import DeleteDialog from './DeleteDialog';
import generateBase64Encode from '../utils/genBase64Encode';
import AlertMessage from './AlertMessage';
import getErrorMsg from '../utils/getErrorMsg';
import posthog from '../utils/posthog';
import { Button, useMediaQuery, IconButton, Typography, Box } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import PublishIcon from '@mui/icons-material/Publish';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import FaceIcon from '@mui/icons-material/Face';
import { Capacitor } from '@capacitor/core';

interface UpdateAvatarFormProps {
  closeModal: () => void;
}

const UpdateAvatarForm: FC<UpdateAvatarFormProps> = ({ closeModal }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm')) || Capacitor.getPlatform() !== 'web';
  const imagePreviewSx = { display: 'flex', justifyContent: 'center', marginTop: 'var(--space-stack)' };
  const currentAvatarSx = { marginTop: 'var(--space-stack-normal)', display: 'flex', justifyContent: 'center' };
  const currentAvatarTextSx = { marginRight: 'var(--space-stack-tight)' };
  const imageBtnsWrapperSx = { display: 'flex', alignItems: 'flex-end' };
  const selectBtnSx = { textTransform: 'var(--space-none)' };
  const clearSelectionBtnSx = { p: 'var(--space-inline)' };
  const submitButtonSx = { marginTop: 'var(--space-form-submit-gap)' };
  const dispatch = useAppDispatch();
  const user = useAppSelector((state) => state.user) as { id: string; username: string; avatar?: { exists?: boolean; imageLink?: string } } | null;
  const [avatarInput, setAvatarInput] = useState('');
  const [fileName, setFileName] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }
    setFileName(file.name);
    generateBase64Encode(file, setAvatarInput, true);
  };

  const clearfileSelection = () => {
    setAvatarInput('');
    setFileName('');
  };

  const handleAvatarUpload = async () => {
    if (avatarInput === '') {
      setError('Select an image file first.');
      return;
    }

    try {
      setIsLoading(true);
      await dispatch(setAvatar(avatarInput));
      setIsLoading(false);

      posthog.capture('avatar_updated');
      dispatch(notify('Successfully updated the avatar!', 'success'));
      setAvatarInput('');
      setFileName('');
      closeModal();
    } catch (err) {
      setIsLoading(false);
      setError(getErrorMsg(err));
    }
  };

  const handleRemoveAvatar = async () => {
    try {
      await dispatch(deleteAvatar());
      posthog.capture('avatar_removed');
      dispatch(notify('Removed avatar.', 'success'));
    } catch (err) {
      setError(getErrorMsg(err));
    }
  };

  return (
    <Box>
      {user?.avatar?.exists && (
        <div>
          <Box sx={imagePreviewSx}>
            <img alt={`${user.username}-avatar`} src={user.avatar.imageLink || ''} width={150} />
          </Box>
          <Box sx={currentAvatarSx}>
            <Typography variant="h6" color="secondary" sx={currentAvatarTextSx}>
              Current Avatar
            </Typography>
            <DeleteDialog type="avatar" handleDelete={handleRemoveAvatar} />
          </Box>
        </div>
      )}
      <Box sx={imageBtnsWrapperSx}>
        <input
          type="file"
          id="image-upload"
          accept="image/*"
          hidden
          onChange={handleFileInputChange}
        />
        <Button
          component="label"
          htmlFor="image-upload"
          variant="outlined"
          color="primary"
          fullWidth
          startIcon={avatarInput ? <CheckCircleIcon /> : <PublishIcon />}
          sx={selectBtnSx}
        >
          {avatarInput ? `${isMobile ? '' : 'Selected '}"${fileName}"` : 'Select Image'}
        </Button>
        {avatarInput && (
          <IconButton
            onClick={clearfileSelection}
            color="secondary"
            size={isMobile ? 'small' : 'medium'}
            sx={clearSelectionBtnSx}
          >
            <CancelIcon />
          </IconButton>
        )}
      </Box>
      {avatarInput && (
        <Box sx={imagePreviewSx}>
          <img alt={fileName} src={avatarInput} width={isMobile ? 250 : 350} />
        </Box>
      )}
      <Button
        size={isMobile ? 'medium' : 'large'}
        variant="contained"
        color="secondary"
        sx={submitButtonSx}
        fullWidth
        startIcon={<FaceIcon />}
        onClick={handleAvatarUpload}
        disabled={isLoading}
      >
        {user?.avatar?.exists ? (isLoading ? 'Updating' : 'Update avatar') : isLoading ? 'Adding' : 'Add avatar'}
      </Button>
      <AlertMessage error={error} severity="error" clearError={() => setError(null)} />
    </Box>
  );
};

export default UpdateAvatarForm;
